import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { ApiPlayable } from "@hive/sdk";
import { catalogueFeedLayout, type CatalogueFeedLayout } from "../../lib/catalogueFeedLayout";
import { mosaicColumns, type CatalogueTile } from "../../lib/catalogueLayout";
import { type CatalogueLevel, type CataloguePlayable } from "../../lib/catalogueLevel";
import { loadCatalogueLevels } from "../../lib/loadCatalogueLevels";
import { PlayableGridSkeleton } from "../playable/PlayableGridSkeleton";
import { MosaicTiles } from "./MosaicTiles";

export function ContinuousCatalogueGrid({ items, seed, pattern, card }: {
	items: CataloguePlayable[];
	seed: string;
	pattern?: number;
	card: (tile: CatalogueTile<ApiPlayable>) => ReactNode;
}) {
	const [levels, setLevels] = useState<Record<string, CatalogueLevel | null>>({});
	const requested = useRef(new Set<string>());
	const mounted = useRef(false);
	useEffect(() => {
		mounted.current = true;
		return () => { mounted.current = false; };
	}, []);
	const unresolved = items.filter((item) => item.catalogueLevel === undefined && !item.spec && !Object.hasOwn(levels, item.id));
	const key = unresolved.map((item) => item.id).join(",");
	useEffect(() => {
		if (!key) return;
		const ids = key.split(",").filter((id) => !requested.current.has(id));
		if (!ids.length) return;
		ids.forEach((id) => requested.current.add(id));
		// An overlapping page must not cancel or repeat an earlier grade request.
		void loadCatalogueLevels(ids).then((next) => {
			if (mounted.current) setLevels((current) => ({ ...current, ...next }));
		});
	}, [key]);
	// Keep settled content mounted while only the appended games wait for their grades.
	const pendingIndex = unresolved.length ? items.indexOf(unresolved[0]!) : items.length;
	const ready = items.slice(0, pendingIndex).map((item) => item.catalogueLevel === undefined && !item.spec && Object.hasOwn(levels, item.id)
		? { ...item, catalogueLevel: levels[item.id] } : item);
	return <div className="catalogue-gallery">
		<ContinuousMosaic items={ready} seed={seed} pattern={pattern} card={card} />
		{pendingIndex < items.length ? <PlayableGridSkeleton className="discover-category-grid" /> : null}
	</div>;
}

function ContinuousMosaic({ items, seed, pattern, card }: {
	items: ApiPlayable[];
	seed: string;
	pattern?: number;
	card: (tile: CatalogueTile<ApiPlayable>) => ReactNode;
}) {
	const ref = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(0);
	const [previous, setPrevious] = useState<CatalogueFeedLayout>();
	useLayoutEffect(() => {
		const element = ref.current!;
		const update = () => setWidth(Math.round(element.clientWidth));
		update();
		const observer = new ResizeObserver(update);
		observer.observe(element);
		return () => observer.disconnect();
	}, []);
	const layout = catalogueFeedLayout(items, { columns: mosaicColumns(width || 1188), seed, pattern, compact: width > 0 && width < 640 }, previous);
	// React retries this component before committing. Earlier bands retain their geometry.
	if (!previous || JSON.stringify(previous.options) !== JSON.stringify(layout.options) ||
		previous.sourceKeys.join("|") !== layout.sourceKeys.join("|")) setPrevious(layout);
	return <div ref={ref} className="catalogue-feed">
		{[...layout.bands, ...(layout.tail ? [layout.tail] : [])].map((band, index) =>
			<div className="quality-mosaic-viewport" key={index} data-feed-band={index} data-complete={index < layout.bands.length}>
				<MosaicTiles layout={band} playable={(item) => item} captioned
					renderCard={(item, sourceIndex, size) => card({ item, sourceIndex, size })} />
			</div>)}
	</div>;
}
